import { useState } from 'react'
import { ConvexClient } from 'convex/browser'
import { api } from '@aurora/convex/convex/_generated/api'
import { setToken } from '../sync/ConvexSync'
import { seedLocalDemo } from '../lib/localDemo'

/**
 * First run: sign in, start a family, or join one with a code.
 *
 * One account is one family. The second parent joins with a six-character
 * code the first parent reads to them — no invite emails. Each parent picks
 * how they appear in the app (mom, dad, or anything else).
 */

type Step = 'choose' | 'signin' | 'create' | 'join' | 'child' | 'code'

export function Welcome({ convexUrl, needsChild }: { convexUrl: string; needsChild?: boolean }) {
  const [step, setStep] = useState<Step>(needsChild ? 'child' : 'choose')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [joinCode, setJoinCode] = useState<string | null>(null)

  // form state
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [label, setLabel] = useState('mom')
  const [code, setCode] = useState('')
  const [childName, setChildName] = useState('')
  const [birthDate, setBirthDate] = useState('')
  const [pronouns, setPronouns] = useState<'she' | 'he' | 'they'>('they')

  const client = () => new ConvexClient(convexUrl)

  const run = async (fn: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
    } catch (e: any) {
      setError(String(e?.message ?? e).replace(/^.*Error: /, ''))
      setBusy(false)
    }
  }

  const signIn = () =>
    run(async () => {
      const c = client()
      const res = await c.mutation(api.authApi.signIn, { email, password })
      setToken(res.token)
      window.location.reload()
    })

  const create = () =>
    run(async () => {
      const c = client()
      const res = await c.mutation(api.authApi.signUp, {
        email, password, name, label,
      })
      setToken(res.token)
      setJoinCode(res.joinCode ?? null)
      setStep('child')
      setBusy(false)
    })

  const join = () =>
    run(async () => {
      const c = client()
      const res = await c.mutation(api.authApi.signUp, {
        email, password, name, label, joinCode: code,
      })
      setToken(res.token)
      window.location.reload()
    })

  const addChild = () =>
    run(async () => {
      const token = localStorage.getItem('aurora:token')
      if (!token) throw new Error('Session expired — sign in again.')
      const P = {
        she: { subject: 'she', object: 'her', possessive: 'her' },
        he: { subject: 'he', object: 'him', possessive: 'his' },
        they: { subject: 'they', object: 'them', possessive: 'their' },
      }[pronouns]
      const c = client()
      await c.mutation(api.authApi.addChild, {
        token,
        name: childName,
        birthDate,
        pronouns: P,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
      if (joinCode) setStep('code')
      else window.location.reload()
      setBusy(false)
    })

  const startDemo = () =>
    run(async () => {
      if (!convexUrl) {
        setToken('local-demo')
        seedLocalDemo()
        window.location.reload()
        return
      }
      const c = client()
      const { token } = await c.mutation(api.seed.createDemoAccount, {})
      setToken(token)
      window.location.reload()
    })

  return (
    <div className="welcome">
      <div className="welcome-inner">
        <div className="welcome-mark" aria-hidden="true">
          <svg width="72" height="72" viewBox="0 0 24 24" fill="none">
            <defs>
              <linearGradient id="w-grad" x1="2" y1="20" x2="22" y2="4">
                <stop offset="0%" stopColor="var(--accent-teal)" />
                <stop offset="55%" stopColor="var(--accent-violet)" />
                <stop offset="100%" stopColor="var(--accent-rose)" />
              </linearGradient>
            </defs>
            <path d="M3 17c3.5-9 6.5-9 9-4.5S18.5 20 21 11"
              stroke="url(#w-grad)" strokeWidth="1.6" strokeLinecap="round" />
            <path d="M3 21c3.5-6 6.5-6 9-3s6.5 5 9-4"
              stroke="url(#w-grad)" strokeWidth="1" strokeLinecap="round" opacity="0.4" />
          </svg>
        </div>

        {step === 'choose' && (
          <>
            <h1 className="welcome-title">Aurora</h1>
            <p className="welcome-tagline">A thinking partner for raising a child deliberately.</p>
            <p className="welcome-body">
              Talk to it the way you would a thoughtful friend who happens to
              remember everything, notice what you cannot, and make whatever you need.
            </p>
            <div className="welcome-actions" style={{ flexDirection: 'column', gap: 'var(--s-2)' }}>
              <button className="btn btn-primary" onClick={() => setStep('signin')}>Sign in</button>
              <button className="btn btn-ghost" onClick={() => setStep('create')}>Start a family</button>
              <button className="btn btn-ghost" onClick={() => setStep('join')}>Join with a code</button>
              <button className="btn btn-quiet" onClick={startDemo} disabled={busy}>
                {busy ? 'Setting up…' : 'Try the demo'}
              </button>
            </div>
          </>
        )}

        {step === 'signin' && (
          <AuthForm
            title="Welcome back"
            fields={
              <>
                <input className="field" type="email" placeholder="Email" value={email}
                  onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
                <input className="field" type="password" placeholder="Password" value={password}
                  onChange={(e) => setPassword(e.target.value)} autoComplete="current-password"
                  onKeyDown={(e) => e.key === 'Enter' && signIn()} />
              </>
            }
            action="Sign in" busy={busy} onSubmit={signIn} onBack={() => setStep('choose')}
          />
        )}

        {step === 'create' && (
          <AuthForm
            title="Start your family"
            fields={
              <>
                <input className="field" placeholder="Your name" value={name}
                  onChange={(e) => setName(e.target.value)} autoComplete="name" />
                <LabelPicker value={label} onChange={setLabel} />
                <input className="field" type="email" placeholder="Email" value={email}
                  onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
                <input className="field" type="password" placeholder="Password (8+ characters)"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password" />
              </>
            }
            action="Create account" busy={busy} onSubmit={create} onBack={() => setStep('choose')}
          />
        )}

        {step === 'join' && (
          <AuthForm
            title="Join your family"
            hint="Ask the parent who set things up for the six-character family code."
            fields={
              <>
                <input className="field field-code" placeholder="FAMILY CODE" value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={6} />
                <input className="field" placeholder="Your name" value={name}
                  onChange={(e) => setName(e.target.value)} autoComplete="name" />
                <LabelPicker value={label} onChange={setLabel} />
                <input className="field" type="email" placeholder="Email" value={email}
                  onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
                <input className="field" type="password" placeholder="Password (8+ characters)"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password" />
              </>
            }
            action="Join family" busy={busy} onSubmit={join} onBack={() => setStep('choose')}
          />
        )}

        {step === 'child' && (
          <AuthForm
            title="Who is this for?"
            hint="Pronouns are yours to choose — nothing is ever guessed from a name."
            fields={
              <>
                <input className="field" placeholder="Child's name" value={childName}
                  onChange={(e) => setChildName(e.target.value)} />
                <input className="field" type="date" value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)} aria-label="Birth date" />
                <div className="seg">
                  {(['she', 'he', 'they'] as const).map((p) => (
                    <button key={p}
                      className={`seg-item${pronouns === p ? ' is-active' : ''}`}
                      onClick={() => setPronouns(p)}>
                      {p === 'she' ? 'she/her' : p === 'he' ? 'he/him' : 'they/them'}
                    </button>
                  ))}
                </div>
              </>
            }
            action="Add child" busy={busy} onSubmit={addChild}
            onBack={needsChild ? undefined : () => setStep('choose')}
          />
        )}

        {step === 'code' && (
          <>
            <h1 className="welcome-title">You're in</h1>
            <p className="welcome-body">
              To add the other parent, have them choose <strong>Join with a code</strong> and
              type this:
            </p>
            <p className="join-code">{joinCode}</p>
            <p className="welcome-body">It's also in the Child tab whenever you need it.</p>
            <div className="welcome-actions">
              <button className="btn btn-primary" onClick={() => window.location.reload()}>
                Open Aurora
              </button>
            </div>
          </>
        )}

        {error && <p className="welcome-error">{error}</p>}
      </div>
    </div>
  )
}

function AuthForm({
  title, hint, fields, action, busy, onSubmit, onBack,
}: {
  title: string
  hint?: string
  fields: React.ReactNode
  action: string
  busy: boolean
  onSubmit: () => void
  onBack?: () => void
}) {
  return (
    <>
      <h1 className="welcome-title" style={{ fontSize: 'var(--text-xl)' }}>{title}</h1>
      {hint && <p className="welcome-body">{hint}</p>}
      <div className="auth-fields">{fields}</div>
      <div className="welcome-actions" style={{ flexDirection: 'column', gap: 'var(--s-2)' }}>
        <button className="btn btn-primary" onClick={onSubmit} disabled={busy}>
          {busy ? 'One moment…' : action}
        </button>
        {onBack && <button className="btn btn-quiet" onClick={onBack}>Back</button>}
      </div>
    </>
  )
}

/** mom / dad / write your own — how this parent appears in the app. */
function LabelPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const custom = value !== 'mom' && value !== 'dad'
  return (
    <div className="seg">
      {['mom', 'dad'].map((l) => (
        <button key={l} className={`seg-item${value === l ? ' is-active' : ''}`}
          onClick={() => onChange(l)}>
          {l}
        </button>
      ))}
      <input
        className={`seg-input${custom ? ' is-active' : ''}`}
        placeholder="other…"
        value={custom ? value : ''}
        onChange={(e) => onChange(e.target.value || 'mom')}
      />
    </div>
  )
}
